import { isoToDateInput, maxCalendarDaysPerRequest, normalizeBars } from '../engine/bars';

export const MAX_BACKTEST_BARS = 50000;

/** Weekend or long weekend between requested firstdate and the first bar we actually got. */
const SESSION_GAP_DAYS = 3;

function barStamp(bar) {
  return bar?.TimeStamp || bar?.timestamp || bar?.TradeTime;
}

function earliestStamp(rawBars) {
  if (!rawBars?.length) return null;
  let min = null;
  for (const bar of rawBars) {
    const ms = Date.parse(barStamp(bar));
    if (!Number.isFinite(ms)) continue;
    if (min == null || ms < min) min = ms;
  }
  return min;
}

function shiftIsoDate(iso, days) {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + days);
  return isoToDateInput(d.toISOString());
}

function calendarDaysBetween(fromIso, toIso) {
  const start = Date.parse(`${fromIso}T12:00:00Z`);
  const end = Date.parse(`${toIso}T12:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return Number.POSITIVE_INFINITY;
  return Math.round((end - start) / 86400000);
}

function maxIsoDate(a, b) {
  if (!a) return b;
  if (!b) return a;
  return a >= b ? a : b;
}

export function isNoBarDataError(err) {
  if (!err) return false;
  if (err.status === 404) return true;
  const msg = String(err.message || err.response?.Message || '');
  return /no data available/i.test(msg);
}

export function isHistoryLimitError(err) {
  if (!err) return false;
  const msg = String(err.message || err.response?.Message || '');
  return /history limit|57600|bars is allowed/i.test(msg);
}

/**
 * Page historical bars backward until firstdate is covered, the API repeats, or MAX is hit.
 * Each request uses a bounded firstdate so a single call stays under TradeStation's 57600-bar cap.
 */
export async function loadBarsPaged({
  fetchBars,
  ticker,
  interval,
  unit,
  firstdate,
  lastdate,
  sessionTemplate = 'Default',
  maxBars = MAX_BACKTEST_BARS,
  onProgress,
} = {}) {
  if (typeof fetchBars !== 'function') {
    throw new Error('fetchBars is required');
  }
  const merged = [];
  const seen = new Set();
  let cursorLast = lastdate;
  let truncated = false;
  let pages = 0;
  let windowDays = maxCalendarDaysPerRequest(interval, unit);

  while (merged.length < maxBars) {
    pages += 1;
    const windowStart = shiftIsoDate(cursorLast, -windowDays);
    const pageFirst = maxIsoDate(firstdate, windowStart);
    let data;
    try {
      data = await fetchBars({
        ticker,
        interval,
        unit,
        firstdate: pageFirst,
        lastdate: cursorLast,
        sessionTemplate,
      });
    } catch (err) {
      if (isHistoryLimitError(err) && windowDays > 1) {
        windowDays = Math.max(1, Math.floor(windowDays / 2));
        pages -= 1;
        continue;
      }
      if (merged.length > 0 && isNoBarDataError(err)) break;
      throw err;
    }
    const page = Array.isArray(data?.Bars) ? data.Bars : [];
    if (!page.length) break;

    let added = 0;
    for (const raw of page) {
      const stamp = barStamp(raw);
      const key = stamp ? String(stamp) : JSON.stringify(raw);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(raw);
      added += 1;
    }

    onProgress?.({ bars: merged.length, pages });
    if (added === 0) break;
    if (merged.length >= maxBars) {
      truncated = true;
      break;
    }

    const earliest = earliestStamp(page);
    if (earliest == null) break;
    const earliestDate = isoToDateInput(new Date(earliest).toISOString());
    if (firstdate && earliestDate <= firstdate) break;
    if (firstdate && calendarDaysBetween(firstdate, earliestDate) <= SESSION_GAP_DAYS) break;

    const nextLast = shiftIsoDate(earliestDate, -1);
    if (!nextLast || nextLast === cursorLast) break;
    if (firstdate && nextLast < firstdate) break;
    cursorLast = nextLast;
    if (pages > 40) {
      truncated = true;
      break;
    }
  }

  const bars = normalizeBars(merged, unit);
  if (bars.length > maxBars) {
    truncated = true;
    return { bars: bars.slice(bars.length - maxBars), truncated, rawCount: merged.length };
  }
  return { bars, truncated, rawCount: merged.length };
}
